/**
 * Data hooks for the workspace-wide Conversations inbox (ENG-35807).
 *
 *   - `GET /api/conversations` → every conversation in the workspace,
 *     newest-first, cursor-paginated (the cross-contact inbox list).
 *
 * The transcript + trace fusion for a single conversation is composed from
 * the existing `useSessionMessages` / `useRuns` / `useRun` hooks — see
 * `../conversations/use-conversation-detail`.
 */
import { useInfiniteQuery } from "@tanstack/react-query";

import { apiRequest } from "./client";
import type { ConversationListResponse } from "./types";

export const conversationKeys = {
  all: ["conversations"] as const,
  list: (slug: string) => ["conversations", "list", slug] as const,
};

/**
 * Conversations refetch poll. There is no realtime channel into the
 * dashboard yet, so the inbox stays current by re-fetching the first page
 * on an interval (and on window refocus). `last_activity_at` is bumped on
 * every message, so a poll re-orders the list to surface freshly-active
 * conversations at the top.
 */
const CONVERSATIONS_POLL_MS = 15_000;

/**
 * Infinite list of all conversations in the active workspace. The endpoint
 * is scoped to the workspace via the session JWT, so `slug` only namespaces
 * the cache. Pages are appended via `fetchNextPage` (the inbox "Load more").
 */
export function useWorkspaceConversations(slug: string, limit?: number) {
  return useInfiniteQuery({
    queryKey: [...conversationKeys.list(slug), limit] as const,
    queryFn: ({ pageParam }) =>
      apiRequest<ConversationListResponse>("GET", "/api/conversations", undefined, {
        cursor: pageParam,
        limit,
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(slug),
    refetchInterval: CONVERSATIONS_POLL_MS,
    refetchOnWindowFocus: true,
  });
}
